"use client";

import { useEffect, useState } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import AngleDown from "@/icons/angleDown.svg";
import MenuIcon from "@/icons/menu.svg";
import LucyWhite from "@/icons/lucy-white.svg";
import LucyLogo from "@/icons/lucy.svg";
import Close from "@/icons/close.svg";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { getTranslation } from "@/i18n/get-translation";
import { convertUrlString } from "@/utils/convert-url-string";
import LearnMoreIcon from "@/icons/learnMore.svg";
import { scrollToElement } from "@/utils/utils";

interface INavigation {
  name: string;
  href: string;
  inline?: boolean;
  inlineId?: string;
}

interface IHeader {
  locale: string;
  whiteBgHeader?: boolean;
  isErrorPage?: boolean;
}
export default function Header({
  locale,
  whiteBgHeader = false,
  isErrorPage = false,
}: IHeader) {
  const t = getTranslation(locale, "header");

  const pathname = usePathname();
  const [isScrolled, setIsScrolled] = useState(false);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navigation: INavigation[] = [
    { name: "whatIsLucy", href: `#what_is_lucy`, inline: true, inlineId: 'what_is_lucy' },
    { name: "whyLucy", href: `#why_is_lucy`, inline: true, inlineId: 'why_is_lucy' },
    { name: "faqs", href: `/${locale}/faq` },
    { name: "blog", href: `/${locale}/blog` },
    { name: "contact", href: `/${locale}/contact-us` },
  ];

  const whiteHeaderPage = [
    "faq",
    "privacy-policy",
    "sitemap",
    "terms-condition",
    "medical-appointment-software",
    "health-assistant-software",
    "medical-report-record-software",
    "medical-prescription-software",
    "medical-billing-software",
  ];

  useEffect(() => {
    window.addEventListener("scroll", () => {
      window.scrollY > 50 ? setIsScrolled(true) : setIsScrolled(false);
    });
  });

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const renderActiveLanguage = () => {
    switch (pathname.substring(0, 3)) {
      case "/en":
        return t("en");
      case "/de":
        return t("de");
      case "/fr":
        return t("fr");
    }
  };

  return (
    <header
      className={`inset-x-0 z-50 text-xl fixed top-0 left-0 w-full transition-all max-md:text-primary-800 ${
        isScrolled || isErrorPage
          ? `text-primary-800 bg-white shadow-200`
          : `text-white shadow-none bg-transparent ${
              whiteHeaderPage.includes(pathname.split("/")[2]) &&
              `bg-white! text-primary-800! shadow-200!`
            }`
      }`}
      id="top"
    >
      <nav
        className="flex items-center p-4 px-20 max-md:px-7 relative z-40"
        aria-label="Global"
      >
        <div className="flex">
          <Link href="/" className="">
            <LucyLogo className="w-[6.563rem] max-md:w-20 h-auto" aria-label="Lucy" role="img" />
          </Link>
        </div>
        <div
          className={`flex items-center gap-12 max-3xl:gap-8 
            ${
              isScrolled || isErrorPage
                ? `drop-shadow-none`
                : `${
                    whiteHeaderPage.includes(pathname.split("/")[2]) ?
                    `drop-shadow-none` : `drop-shadow-[0_0.125rem_0.125rem_rgba(0,0,0,0.4)]`
                  }`
            }
            ${
            locale === "fr-lu" && "gap-[1.9rem]! max-3xl:gap-[1.2rem]"
          }  ml-auto max-md:hidden`}
        >
          {navigation.map((item: INavigation) => (
            <Link
              key={item.name}
              href={`${item.href}`}
              className="hover:opacity-80"
              onClick={()=>item?.inlineId && scrollToElement(item.inlineId)}
            >
              {t(item.name)}
            </Link>
          ))}
          <Link
            href={`/${locale}/request-demo`}
            className="relative rounded-full px-5 py-4 leading-4 hover:bg-(image:--primary-gradient) -ml-2 overflow-hidden header-grad-btn hover:text-white"
          >
            <span className="absolute inset-0 rounded-full border border-transparent header-grad-btn-hover:border-grad -z-10"></span>
            {t("requestDemo")}
          </Link>
        </div>
        <div className={`relative ml-8 max-md:ml-auto
        ${
          isScrolled || isErrorPage
            ? `drop-shadow-none`
            : `${
                whiteHeaderPage.includes(pathname.split("/")[2]) ?
                `drop-shadow-none` : `drop-shadow-[0_0.125rem_0.125rem_rgba(0,0,0,0.4)]`
              }`
        }
          `}>
          <Menu>
            <MenuButton id="lang-menu-desktop" className="flex items-center text-inherit hover:opacity-80 aria-expanded:opacity-80 w-12 h-12 justify-center cursor-pointer">
              {renderActiveLanguage()}
              <AngleDown className="mt-1 ml-2 w-3.5" />
            </MenuButton>
            <MenuItems modal={false} anchor="bottom" className="z-99 py-1 px-3 bg-white rounded text-primary-900 border-2 border-primary-400 flex flex-col text-xl">
              {pathname.substring(0, 3) !== "/en" && (
                <MenuItem>
                  <Link
                    className="w-12 h-12 justify-center flex items-center"
                    href={`/en-lu${convertUrlString(pathname)}`}
                  >
                    {t("en")}
                  </Link>
                </MenuItem>
              )}
              {pathname.substring(0, 3) !== "/de" && (
                <MenuItem>
                  <Link
                    className="w-12 h-12 justify-center flex items-center"
                    href={`/de-lu${convertUrlString(pathname)}`}
                  >
                    {t("de")}
                  </Link>
                </MenuItem>
              )}
              {pathname.substring(0, 3) !== "/fr" && (
                <MenuItem>
                  <Link
                    className="w-12 h-12 justify-center flex items-center"
                    href={`/fr-lu${convertUrlString(pathname)}`}
                  >
                    {t("fr")}
                  </Link>
                </MenuItem>
              )}
            </MenuItems>
          </Menu>
        </div>
        <div className="hidden max-md:block" onClick={handleMenuToggle}>
          <button className="ml-8 w-12 h-12 -mr-3 flex items-center justify-center" aria-label="Open menu" aria-expanded={isMenuOpen}>
            <MenuIcon />
          </button>
        </div>
      </nav>
      <nav
        className={`w-full z-50 absolute top-0 left-0 bg-white/70 h-screen ${
          isMenuOpen ? "block" : "hidden"
        }`}
      >
        <Image
          width={1728}
          height={2692}
          loading="lazy"
          className="absolute top-0 -right-[50%] z-10 w-[110%] max-w-none opacity-30 -translate-y-[20%]"
          src="/images/dna-white.webp"
          alt=""
        />
        <div className="absolute h-full -top-[53vw] max-sm:-top-[15vw] left-0 right-0">
          <Image
            width={375}
            height={630}
            loading="lazy"
            className="absolute top-0 right-0 w-full"
            src="/icons/menu-left.svg"
            alt=""
          />
          <Image
            width={375}
            height={687}
            loading="lazy"
            className="absolute top-0 right-0 w-full"
            src="/icons/menu-right.svg"
            alt=""
          />
        </div>
        {/* Mobile menu */}
        <div
          className={`flex flex-col relative z-10 text-white text-xl px-7 py-4`}
        >
          <div className="mb-9 flex justify-between items-center">
            <LucyWhite className="w-[6.563rem] h-[3.938rem] max-md:w-20" />
            <div className="relative ml-8 max-md:ml-auto">
              <Menu>
                <MenuButton id="lang-menu-mobile" className="flex items-center text-inherit hover:opacity-80 aria-expanded:opacity-80 w-12 h-12 justify-center">
                  {renderActiveLanguage()}
                  <AngleDown className="mt-1 ml-2 w-3.5" />
                </MenuButton>
                <MenuItems className="absolute right-0 py-1 px-3 bg-white rounded text-primary-900 border-2 border-primary-400">
                  {pathname.substring(0, 3) !== "/en" && (
                    <MenuItem>
                      <Link
                        className="w-12 h-12 justify-center flex items-center"
                        href={`/en-lu${convertUrlString(pathname)}`}
                      >
                        {t("en")}
                      </Link>
                    </MenuItem>
                  )}
                  {pathname.substring(0, 3) !== "/de" && (
                    <MenuItem>
                      <Link
                        className="w-12 h-12 justify-center flex items-center"
                        href={`/de-lu${convertUrlString(pathname)}`}
                      >
                        {t("de")}
                      </Link>
                    </MenuItem>
                  )}
                  {pathname.substring(0, 3) !== "/fr" && (
                    <MenuItem>
                      <Link
                        className="w-12 h-12 justify-center flex items-center"
                        href={`/fr-lu${convertUrlString(pathname)}`}
                      >
                        {t("fr")}
                      </Link>
                    </MenuItem>
                  )}
                </MenuItems>
              </Menu>
            </div>
            <div className="hidden max-md:block">
              <button
                className="ml-8 w-12 h-12 -mr-3 flex items-center justify-center"
                onClick={handleMenuToggle}
                aria-label="Close menu"
              >
                <Close />
              </button>
            </div>
          </div>
          {navigation.map((item: INavigation) => (
            <Link
              key={item.name}
              href={`${item.href}`}
              className="mb-6 hover:opacity-80"
            >
              {t(item.name)}
            </Link>
          ))}
          <Link
            href={`/${locale}/request-demo`}
            className="text-white bg-(image:--primary-gradient) max-w-max rounded-full pl-5 pr-2 py-1.5 leading-4 flex items-center"
          >
            {t("requestDemo")}
            <div className="relative ml-2">
              <div className="bg-primary-400 text-white rounded-full w-8 h-8 opacity-[0.24]"></div>
              <LearnMoreIcon className="w-2.5 text-white font-bold absolute left-2/4 top-2/4 transform-[translate(-50%,-50%)]" />
            </div>
          </Link>
        </div>
      </nav>
    </header>
  );
}
